import React, { useEffect, useState } from 'react';

export interface NotificationProps {
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message?: string;
    duration?: number;
    onClose?: () => void;
    theme: any;
}

export const Notification: React.FC<NotificationProps> = ({
    type,
    title,
    message,
    duration = 3000,
    onClose,
    theme
}) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            handleClose();
        }, duration);

        return () => clearTimeout(timer);
    }, [duration]);

    const handleClose = () => {
        setIsAnimating(true);
        setTimeout(() => {
            setIsVisible(false);
            onClose?.();
        }, 300);
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return '✅';
            case 'error':
                return '❌';
            case 'warning':
                return '⚠️';
            case 'info':
                return 'ℹ️';
            default:
                return 'ℹ️';
        }
    };

    const getBackgroundColor = () => {
        switch (type) {
            case 'success':
                return theme.colors.success + '20';
            case 'error':
                return theme.colors.error + '20';
            case 'warning':
                return theme.colors.warning + '20';
            case 'info':
                return theme.colors.primary + '20';
            default:
                return theme.colors.primary + '20';
        }
    };

    const getBorderColor = () => {
        switch (type) {
            case 'success':
                return theme.colors.success;
            case 'error':
                return theme.colors.error;
            case 'warning':
                return theme.colors.warning;
            case 'info':
                return theme.colors.primary;
            default:
                return theme.colors.primary;
        }
    };

    if (!isVisible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            minWidth: '300px',
            maxWidth: '400px',
            backgroundColor: theme.colors.surface,
            border: `2px solid ${getBorderColor()}`,
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            transform: isAnimating ? 'translateX(100%)' : 'translateX(0)',
            transition: 'transform 0.3s ease-in-out',
            backdropFilter: 'blur(10px)',
            borderLeft: `4px solid ${getBorderColor()}`,
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px'
            }}>
                <div style={{
                    fontSize: '20px',
                    flexShrink: 0,
                    marginTop: '2px'
                }}>
                    {getIcon()}
                </div>

                <div style={{ flex: 1 }}>
                    <div style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: theme.colors.text,
                        marginBottom: message ? '4px' : '0'
                    }}>
                        {title}
                    </div>
                    {message && (
                        <div style={{
                            fontSize: '14px',
                            color: theme.colors.textSecondary,
                            lineHeight: '1.4'
                        }}>
                            {message}
                        </div>
                    )}
                </div>

                <button
                    onClick={handleClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '18px',
                        color: theme.colors.textSecondary,
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        flexShrink: 0
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.colors.border;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                >
                    ×
                </button>
            </div>

            {/* Progress bar */}
            <div style={{
                position: 'absolute',
                bottom: '0',
                left: '0',
                right: '0',
                height: '3px',
                backgroundColor: theme.colors.border,
                borderRadius: '0 0 10px 10px',
                overflow: 'hidden'
            }}>
                <div style={{
                    height: '100%',
                    backgroundColor: getBorderColor(),
                    width: '100%',
                    animation: `shrink ${duration}ms linear forwards`
                }} />
            </div>

            <style>{`
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }
            `}</style>
        </div>
    );
}; 